/**
 * F43-FC §6.6 — AffiliationAdminDialog.
 *
 * The single shared dialog for admin affiliation moderation, opened straight
 * from the `AdminUserTabRow` already in hand (id + venue name + status):
 *  - `suspend` mode: an `info` Banner ("forces both visibility flags off +
 *    cancels future bookings") + a mandatory reason.
 *  - `end` mode (terminal): a `warning` Banner ("cancels the trainer's future
 *    bookings at this venue") + a mandatory reason + a type-to-confirm `END`
 *    phrase (an end is irreversible — `assertTransition` has no path out).
 *
 * Composes F02 primitives only (Modal / FormField / Textarea / TextInput /
 * Button / Banner) — the `ModerateEndorsementDialog` precedent: `Modal` with a
 * custom `footer` so the confirm button carries the test-id + the gating logic.
 * `onConfirm` REJECTS on failure to keep the dialog open + surface the error
 * (the `ForceTransitionDialog` re-throw contract, §6.2).
 */
import { useEffect, useState } from 'react';
import { Banner, Button, FormField, Modal, TextInput, Textarea } from '@bravemove/ui-web';
import { useI18n } from '@bravemove/i18n/react';

export interface AffiliationAdminDialogProps {
  readonly open: boolean;
  readonly mode: 'suspend' | 'end';
  readonly venueName: string;
  readonly onClose: () => void;
  /** Resolves on success; rejects to keep the dialog open + show the error. */
  readonly onConfirm: (reason: string) => Promise<void>;
}

const END_PHRASE = 'END';
const REASON_MAX = 500;

export function AffiliationAdminDialog({
  open,
  mode,
  venueName,
  onClose,
  onConfirm,
}: AffiliationAdminDialogProps) {
  const { t } = useI18n();
  const [reason, setReason] = useState('');
  const [phrase, setPhrase] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset every time the dialog (re)opens or switches mode.
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (!open) return;
    setReason('');
    setPhrase('');
    setError(null);
    setSubmitting(false);
  }, [open, mode]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const reasonOk = reason.trim().length > 0 && reason.length <= REASON_MAX;
  const phraseOk = mode === 'suspend' || phrase.trim().toUpperCase() === END_PHRASE;
  const canConfirm = reasonOk && phraseOk && !submitting;

  const handleConfirm = async () => {
    if (!canConfirm) return;
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm(reason.trim());
      onClose();
    } catch (err) {
      // Keep the dialog OPEN + surface the message (§6.2 contract).
      setError(err instanceof Error ? err.message : String(err));
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="md"
      title={
        mode === 'suspend'
          ? t('affiliation:admin.suspend_title', { defaultValue: 'Suspend affiliation' })
          : t('affiliation:admin.end_title', { defaultValue: 'End affiliation' })
      }
      footer={
        <>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={submitting}
            data-testid="f43-affiliation-dialog-cancel"
          >
            {t('affiliation:admin.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button
            variant="destructive"
            data-testid="f43-affiliation-dialog-confirm"
            disabled={!canConfirm}
            loading={submitting}
            onClick={() => void handleConfirm()}
          >
            {mode === 'suspend'
              ? t('affiliation:admin.suspend_cta', { defaultValue: 'Suspend' })
              : t('affiliation:admin.end_cta', { defaultValue: 'End affiliation' })}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3" data-testid="f43-affiliation-admin-dialog">
        <p className="text-[13px] text-ink-3">
          {t('affiliation:admin.target', { defaultValue: 'Venue' })}: {venueName}
        </p>

        {mode === 'end' ? (
          // `Banner` does not forward arbitrary props (no `...rest`), so the
          // test-id lives on a wrapping div.
          <div data-testid="f43-affiliation-end-banner">
            <Banner tone="warning">
              {t('affiliation:admin.end_banner', {
                defaultValue:
                  'Ending this affiliation is terminal and cancels the trainer’s future bookings at this venue.',
              })}
            </Banner>
          </div>
        ) : (
          <Banner tone="info">
            {t('affiliation:admin.suspend_banner', {
              defaultValue:
                'Suspending forces both visibility flags off and cancels the trainer’s future bookings at this venue.',
            })}
          </Banner>
        )}

        <FormField label={t('affiliation:admin.reason_label', { defaultValue: 'Reason' })} required>
          <Textarea
            data-testid="f43-affiliation-dialog-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={REASON_MAX}
            rows={3}
          />
        </FormField>

        {mode === 'end' ? (
          <FormField
            label={t('affiliation:admin.confirm_phrase_label', {
              defaultValue: 'Type END to confirm',
            })}
            required
          >
            <TextInput
              data-testid="f43-affiliation-confirm-phrase"
              value={phrase}
              onChange={(e) => setPhrase(e.target.value)}
              autoComplete="off"
            />
          </FormField>
        ) : null}

        {error ? <Banner tone="danger">{error}</Banner> : null}
      </div>
    </Modal>
  );
}
